/**
 * StatsDashboard Container Component
 *
 * 简化的配额使用概览视图。
 * 顶部筛选器 + 表格展示所有模型的配额使用情况。
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { clsx } from 'clsx';

export interface StatsDashboardProps {
  autoRefresh?: boolean;
  refreshInterval?: number;
  className?: string;
}

interface QuotaOverviewItem {
  provider_name: string;
  pool_index: number;
  model_use: string;
  model_name: string;
  quota_type: string;
  limit_value: number;
  used_count: number;
  remaining: number;
  percentage: number;
  reset_at: string | null;
}

interface QuotaOverviewResponse {
  items: QuotaOverviewItem[];
  providers: string[];
  total: number;
  timestamp: string;
}

const quotaTypeLabels: Record<string, string> = {
  concurrency: '并发数',
  token_per_minute: 'Token/分钟',
  token_per_hour: 'Token/小时',
  token_per_day: 'Token/天',
  token_per_month: 'Token/月',
  token_total: 'Token总量',
  request_per_minute: '请求/分钟',
  request_per_hour: '请求/小时',
  request_per_day: '请求/天',
  request_per_month: '请求/月',
  request_total: '请求总量',
};

const formatNumber = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

const formatResetTime = (resetAt: string | null): string => {
  if (!resetAt) return '-';
  try {
    const date = new Date(resetAt);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    if (diffMs <= 0) return '已重置';
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) return `${diffMins}分钟`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}小时`;
    return `${Math.floor(diffHours / 24)}天`;
  } catch {
    return '-';
  }
};

const getBarColor = (percentage: number): string => {
  if (percentage >= 95) return 'bg-red-500';
  if (percentage >= 80) return 'bg-yellow-500';
  return 'bg-green-500';
};

const getTextColor = (percentage: number): string => {
  if (percentage >= 95) return 'text-red-600';
  if (percentage >= 80) return 'text-yellow-600';
  return 'text-green-600';
};

export const StatsDashboard: React.FC<StatsDashboardProps> = ({
  autoRefresh = false,
  refreshInterval = 30000,
  className = '',
}) => {
  const [data, setData] = useState<QuotaOverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [providerFilter, setProviderFilter] = useState<string>('');

  const fetchData = useCallback(async () => {
    setError(null);
    try {
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
      const url = providerFilter
        ? `${API_BASE_URL}/api/admin/llm/stats/quota-overview?provider=${encodeURIComponent(providerFilter)}`
        : `${API_BASE_URL}/api/admin/llm/stats/quota-overview`;

      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error(`请求失败: ${res.status}`);

      const json: QuotaOverviewResponse = await res.json();
      setData(json);
      setLastUpdate(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载配额数据失败');
    } finally {
      setLoading(false);
    }
  }, [providerFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchData, refreshInterval);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, fetchData]);

  const providers = useMemo(() => data?.providers ?? [], [data]);

  const filteredItems = useMemo(() => {
    return data?.items ?? [];
  }, [data]);

  if (loading) {
    return (
      <div className={clsx('p-8 text-center text-gray-500', className)}>
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4" />
        加载配额数据中...
      </div>
    );
  }

  return (
    <div className={clsx('space-y-4', className)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-medium text-gray-800">配额使用概览</h3>
          <select
            value={providerFilter}
            onChange={(e) => setProviderFilter(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">全部供应商</option>
            {providers.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-4">
          {lastUpdate && (
            <span className="text-xs text-gray-500">
              更新于 {lastUpdate.toLocaleTimeString()}
            </span>
          )}
          <button
            type="button"
            onClick={fetchData}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            刷新
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-4 text-red-500 hover:text-red-700"
          >
            关闭
          </button>
        </div>
      )}

      {!error && filteredItems.length === 0 ? (
        <div className="p-8 bg-gray-50 border border-gray-200 rounded-lg text-center text-gray-500 text-sm">
          暂无配额数据
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left font-medium text-gray-700">供应商</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">模型用途</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">模型名称</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">配额类型</th>
                <th className="px-4 py-3 text-right font-medium text-gray-700">总可用</th>
                <th className="px-4 py-3 text-right font-medium text-gray-700">已用</th>
                <th className="px-4 py-3 text-right font-medium text-gray-700">剩余</th>
                <th className="px-4 py-3 text-center font-medium text-gray-700">使用率</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">重置时间</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredItems.map((item, idx) => {
                const pct = item.percentage;
                return (
                  <tr key={`${item.provider_name}-${item.pool_index}-${item.model_use}-${item.quota_type}`} className={clsx('hover:bg-gray-50', idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50')}>
                    <td className="px-4 py-3 text-gray-800 font-medium">{item.provider_name}</td>
                    <td className="px-4 py-3 text-gray-800">{item.model_use}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{item.model_name}</td>
                    <td className="px-4 py-3 text-gray-600">
                      <span className="inline-block px-2 py-0.5 bg-gray-100 rounded text-xs">
                        {quotaTypeLabels[item.quota_type] || item.quota_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-800">{formatNumber(item.limit_value)}</td>
                    <td className="px-4 py-3 text-right text-gray-800 font-medium">{formatNumber(item.used_count)}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={getTextColor(pct)}>{formatNumber(item.remaining)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden min-w-[60px]">
                          <div
                            className={clsx(getBarColor(pct), 'h-full rounded-full transition-all duration-150')}
                            style={{ width: `${Math.min(pct, 100)}%` }}
                          />
                        </div>
                        <span className={clsx(getTextColor(pct), 'text-xs font-medium w-12 text-right')}>
                          {pct.toFixed(1)}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{formatResetTime(item.reset_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {data && (
        <div className="text-xs text-gray-500">
          共 {data.total} 条配额记录
        </div>
      )}
    </div>
  );
};

export default StatsDashboard;
